// Rich — the chat's Markdown renderer.
//
// Pipeline per block: remend (close half-streamed ** ` [ $$) -> marked (GFM) ->
// DOMPurify. The text is split into top-level blocks; finished blocks are memoised, so
// while tokens stream only the last block is re-parsed and nothing above it flickers.
// Enhancements (syntax highlighting, math, mermaid) run only on finished blocks and load
// lazily — a chat that never shows code never downloads highlight.js.
//
// Relative image and link targets are the agent's files in the project folder: a DOMPurify
// hook points them at /api/projects/<id>/raw, which serves them with a sandbox CSP.

import { h, Component } from 'preact';
import { Marked, Renderer } from 'marked';
import remend from 'remend';
import DOMPurify from 'dompurify';

// ─── paths ────────────────────────────────────────────────────────────────────
export function rawUrl(pid, path, download) {
  return `/api/projects/${pid}/raw?path=${encodeURIComponent(path)}${download ? '&download=1' : ''}`;
}

const EXTERNAL = /^(https?:|mailto:|tel:|data:image\/|blob:|#)/i;
function isWorkspacePath(p) {
  if (!p || EXTERNAL.test(p) || p.startsWith('/api/') || p.startsWith('//')) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(p)) return false;          // any other scheme: leave to DOMPurify
  return true;
}
function cleanPath(p) {
  try { p = decodeURIComponent(p); } catch (e) { /* keep as is */ }
  return p.replace(/^\.\//, '').split('#')[0].split('?')[0];
}

let currentPid = null;                    // set synchronously around each sanitize call
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    if (currentPid && isWorkspacePath(src)) node.setAttribute('src', rawUrl(currentPid, cleanPath(src)));
    node.setAttribute('loading', 'lazy');
    node.setAttribute('decoding', 'async');
    if (!node.getAttribute('alt')) node.setAttribute('alt', cleanPath(src).split('/').pop() || 'image');
  }
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    if (currentPid && isWorkspacePath(href)) {
      node.setAttribute('href', rawUrl(currentPid, cleanPath(href)));
      node.setAttribute('data-file', cleanPath(href));
    }
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function sanitize(html, pid) {
  currentPid = pid;
  try {
    return DOMPurify.sanitize(html, {
      ADD_ATTR: ['target', 'loading', 'decoding'],
      FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'iframe', 'object', 'embed'],
    });
  } finally { currentPid = null; }
}

// ─── marked ───────────────────────────────────────────────────────────────────
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const base = new Renderer();

// Math is tokenised BEFORE markdown so `_` and `*` inside formulas are not eaten as emphasis.
const mathExt = {
  extensions: [
    {
      name: 'mathBlock', level: 'block',
      start(src) { const i = src.indexOf('$$'); return i < 0 ? undefined : i; },
      tokenizer(src) {
        const m = /^\$\$([\s\S]+?)\$\$(?:\n|$)/.exec(src);
        if (m) return { type: 'mathBlock', raw: m[0], text: m[1].trim() };
      },
      renderer(t) { return `<div class="md-math md-math-block">${esc(t.text)}</div>`; },
    },
    {
      name: 'mathInline', level: 'inline',
      start(src) { const i = src.indexOf('$'); return i < 0 ? undefined : i; },
      tokenizer(src) {
        // $x$ but not "$5 and $10": no space just inside the dollars, no digit right after
        const m = /^\$(?!\s)((?:\\\$|[^$\n])+?)(?<!\s)\$(?!\d)/.exec(src);
        if (m) return { type: 'mathInline', raw: m[0], text: m[1] };
      },
      renderer(t) { return `<span class="md-math">${esc(t.text)}</span>`; },
    },
  ],
};

const md = new Marked({ gfm: true, breaks: false }, mathExt, {
  renderer: {
    code(token) {
      const lang = (token.lang || '').trim().split(/\s+/)[0].toLowerCase();
      if (lang === 'mermaid') {
        // source as escaped text, not an attribute: DOMPurify drops attributes containing "-->"
        return `<div class="md-mermaid"><pre><code>${esc(token.text)}</code></pre></div>`;
      }
      return `<div class="md-code"><div class="md-code-bar"><span>${esc(lang || 'text')}</span>`
        + `<button type="button" data-act="copy-code" aria-label="Copy code">Copy</button></div>`
        + `<pre><code class="${lang ? 'language-' + esc(lang) : 'nohighlight'}">${esc(token.text)}</code></pre></div>`;
    },
    table(token) {
      return `<div class="md-table"><div class="md-table-bar"><span>${token.rows.length} row${token.rows.length === 1 ? '' : 's'}</span>`
        + `<button type="button" data-act="copy-table" aria-label="Copy table">Copy</button></div>`
        + `<div class="md-table-scroll">${base.table.call(this, token)}</div></div>`;
    },
    image(token) {
      const alt = token.text || '';
      return `<span class="md-fig"><img src="${esc(token.href)}" alt="${esc(alt)}" data-act="zoom">`
        + (alt ? `<span class="md-cap">${esc(alt)}</span>` : '') + `</span>`;
    },
  },
});

// ─── blocks ───────────────────────────────────────────────────────────────────
// Split on blank lines that are outside fences and $$ math: each piece is a stable unit.
export function splitBlocks(src) {
  const lines = src.split('\n');
  const out = [];
  let cur = [], fence = null, math = false;
  for (const line of lines) {
    const t = line.trimStart();
    if (fence) {
      cur.push(line);
      if (t.startsWith(fence) && t.slice(fence.length).trim() === '') fence = null;
      continue;
    }
    const f = /^(`{3,}|~{3,})/.exec(t);
    if (f) { fence = f[1]; cur.push(line); continue; }
    if (t.startsWith('$$')) {
      const closes = t.length > 2 && t.trimEnd().endsWith('$$') && t.trim() !== '$$';
      if (!closes) math = !math;
      cur.push(line); continue;
    }
    if (!math && t === '') {
      if (cur.length) { out.push(cur.join('\n')); cur = []; }
      continue;
    }
    cur.push(line);
  }
  if (cur.length) out.push(cur.join('\n'));
  return out;
}

const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

// The block still being written: complete its inline syntax, and do not show a table
// until its separator row exists or a half-typed row at its end.
function prepareTail(block) {
  const lines = block.split('\n');
  if (lines[0].trim().startsWith('|')) {
    if (lines.length < 2 || !TABLE_SEP.test(lines[1])) return { text: block, plain: true };
    const last = lines[lines.length - 1].trim();
    if (lines.length > 2 && !(last.endsWith('|') && last.length > 1)) lines.pop();
    return { text: lines.join('\n') };
  }
  return { text: remend(block) };
}

function renderBlock(text, pid) {
  return sanitize(md.parse(text), pid);
}

// ─── lazy enhancers ───────────────────────────────────────────────────────────
let hljsP = null, katexP = null, mermaidP = null, mermaidN = 0;
const load = {
  hljs: () => (hljsP ||= import('highlight.js/lib/common').then((m) => m.default || m)),
  katex: () => (katexP ||= (ensureCss('https://cdn.jsdelivr.net/npm/katex@0.18.7/dist/katex.min.css'),
    import('katex').then((m) => m.default || m))),
  mermaid: () => (mermaidP ||= import('mermaid').then((m) => {
    const mm = m.default || m;
    mm.initialize({ startOnLoad: false, securityLevel: 'strict',
      theme: document.documentElement.getAttribute('data-theme') === 'light' ? 'default' : 'dark' });
    return mm;
  })),
};

function ensureCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet'; l.href = href; l.crossOrigin = 'anonymous';
  document.head.appendChild(l);
}

export async function enhance(root) {
  if (!root) return;
  const codes = root.querySelectorAll('pre code[class*="language-"]:not([data-hl])');
  if (codes.length) {
    const hljs = await load.hljs();
    codes.forEach((el) => {
      el.setAttribute('data-hl', '1');
      const lang = (el.className.match(/language-([\w+#-]+)/) || [])[1];
      if (lang && !hljs.getLanguage(lang)) return;
      try { hljs.highlightElement(el); } catch (e) { /* leave it plain */ }
    });
  }
  const maths = root.querySelectorAll('.md-math:not([data-done])');
  if (maths.length) {
    const katex = await load.katex();
    maths.forEach((el) => {
      el.setAttribute('data-done', '1');
      try {
        katex.render(el.textContent || '', el,
          { displayMode: el.classList.contains('md-math-block'), throwOnError: false, output: 'htmlAndMathml' });
      } catch (e) { /* the escaped source stays visible */ }
    });
  }
  const mers = root.querySelectorAll('.md-mermaid:not([data-done])');
  if (mers.length) {
    const mermaid = await load.mermaid();
    for (const el of mers) {
      el.setAttribute('data-done', '1');
      try {
        const { svg } = await mermaid.render(`mmd-${++mermaidN}`, (el.querySelector('code') || el).textContent || '');
        el.innerHTML = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true }, ADD_TAGS: ['foreignObject'] });
      } catch (e) {
        // keep the source visible and say why, rather than an empty box
        el.classList.add('md-mermaid-error');
        el.setAttribute('data-error', String((e && e.message) || e).slice(0, 300));
        console.warn('mermaid render failed:', e);
      }
    }
  }
}

// ─── clicks: copy, zoom ───────────────────────────────────────────────────────
export async function copyText(text, btn) {
  try {
    await navigator.clipboard.writeText(text);
    if (btn) { const t = btn.textContent; btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = t; }, 1200); }
  } catch (e) { /* clipboard blocked: nothing to do */ }
}

function tableToTsv(table) {
  return [...table.querySelectorAll('tr')].map((tr) =>
    [...tr.children].map((c) => c.innerText.replace(/\t|\n/g, ' ').trim()).join('\t')).join('\n');
}

let lightbox = null;
export function openLightbox(src, caption) {
  if (!lightbox) {
    lightbox = document.createElement('dialog');
    lightbox.className = 'md-lightbox';
    lightbox.innerHTML = '<button type="button" class="md-lb-close" aria-label="Close">×</button><img alt=""><p></p>';
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox || e.target.classList.contains('md-lb-close')) lightbox.close(); });
    document.body.appendChild(lightbox);
  }
  lightbox.querySelector('img').src = src;
  lightbox.querySelector('img').alt = caption || '';
  lightbox.querySelector('p').textContent = caption || '';
  lightbox.showModal();
}

export function onRichClick(e, onOpenFile) {
  const el = e.target.closest('[data-act], a[data-file]');
  if (!el) return;
  const act = el.getAttribute('data-act');
  if (act === 'copy-code') {
    e.preventDefault();
    copyText(el.closest('.md-code').querySelector('code').innerText, el);
  } else if (act === 'copy-table') {
    e.preventDefault();
    copyText(tableToTsv(el.closest('.md-table').querySelector('table')), el);
  } else if (act === 'zoom') {
    e.preventDefault();
    openLightbox(el.getAttribute('src'), el.getAttribute('alt'));
  } else if (el.hasAttribute('data-file') && onOpenFile && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
    e.preventDefault();
    onOpenFile(el.getAttribute('data-file'));
  }
}

// ─── the component ────────────────────────────────────────────────────────────
// Props: source, pid (project id for file links), streaming, onOpenFile(path)
export class Rich extends Component {
  constructor(props) {
    super(props);
    this.cache = new Map();          // finished block text -> sanitised html
    this.raf = 0;
    this.root = null;
    this.onClick = (e) => onRichClick(e, this.props.onOpenFile);
  }

  shouldComponentUpdate(next) {
    // While streaming, re-render at most once per frame however fast tokens arrive.
    if (!next.streaming || !this.props.streaming) return true;
    if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.forceUpdate(); });
    return false;
  }

  componentWillUnmount() {
    if (this.raf) cancelAnimationFrame(this.raf);
    window.removeEventListener('uai-workspace-awake', this.onAwake);
  }

  componentDidMount() {
    // Images the agent saved live in the workspace; while it sleeps /raw answers 503.
    // Show a quiet placeholder instead of a broken image, and reload once it is awake.
    this.onImgError = (e) => {
      const img = e.target;
      if (img && img.tagName === 'IMG' && img.closest('.md-fig')) img.closest('.md-fig').classList.add('md-fig-missing');
    };
    this.onAwake = () => {
      if (!this.root) return;
      this.root.querySelectorAll('.md-fig-missing img').forEach((img) => {
        img.closest('.md-fig').classList.remove('md-fig-missing');
        const src = img.getAttribute('src');
        img.setAttribute('src', src + (src.includes('?') ? '&' : '?') + 'r=' + Date.now());
      });
    };
    if (this.root) this.root.addEventListener('error', this.onImgError, true);
    window.addEventListener('uai-workspace-awake', this.onAwake);
    this.afterRender();
  }
  componentDidUpdate() { this.afterRender(); }

  afterRender() {
    if (!this.root) return;
    // enhance everything except a block still being written
    const blocks = this.root.querySelectorAll(':scope > .md-block[data-final="1"]:not([data-enh])');
    blocks.forEach((b) => { b.setAttribute('data-enh', '1'); enhance(b); });
  }

  blockHtml(text) {
    let html = this.cache.get(text);
    if (html === undefined) {
      html = renderBlock(text, this.props.pid);
      this.cache.set(text, html);
      if (this.cache.size > 400) this.cache.delete(this.cache.keys().next().value);
    }
    return html;
  }

  render(props) {
    const src = (props.source || '').replace(/<\|[a-z0-9_]{1,32}\|>/g, '');
    const blocks = splitBlocks(src);
    const kids = blocks.map((b, i) => {
      const last = i === blocks.length - 1;
      if (last && props.streaming) {
        const tail = prepareTail(b);
        const html = tail.plain ? `<p>${esc(tail.text)}</p>` : renderBlock(tail.text, props.pid);
        return h('div', { key: 'tail', className: 'md-block', dangerouslySetInnerHTML: { __html: html } });
      }
      return h('div', { key: i + ':' + b.length, className: 'md-block', 'data-final': '1',
        dangerouslySetInnerHTML: { __html: this.blockHtml(b) } });
    });
    return h('div', { className: 'md' + (props.streaming ? ' md-streaming' : ''), ref: (el) => { this.root = el; },
      onClick: this.onClick }, kids);
  }
}
